/* ==========================================================================
   lh-showroom-status.js
   --------------------------------------------------------------------------
   Renders a live "Open now / Closed" line for each showroom, evaluated in
   that showroom's own timezone (so Beverly Hills and New York are independent).

   Reads JSON from [data-lh-hours]:
     {
       "tz":        "America/Los_Angeles",
       "days":      { "mon": "11:00-18:00", ..., "sun": "closed" },
       "overrides": { "2026-12-24": "11:00-15:00", "2026-12-25": "closed" }
     }

   Day values: "HH:MM-HH:MM" | "closed" | "appointment"
   Dated overrides win over the weekly value for that calendar date.

   FAIL-SAFE: the status node starts hidden and is only revealed once a
   status has been computed successfully. A malformed override line, a bad
   timezone or a typo'd time therefore shows nothing at all rather than
   showing something wrong — the static weekly summary is always rendered
   server-side and is unaffected.
   ========================================================================== */
(function () {
  'use strict';

  var DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /* "11:00-18:00" -> {open: 660, close: 1080} in minutes, or null */
  function parseRange(value) {
    if (typeof value !== 'string') return null;
    var m = value.trim().match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    var oh = +m[1], om = +m[2], ch = +m[3], cm = +m[4];
    if (oh > 23 || ch > 23 || om > 59 || cm > 59) return null;
    var open = oh * 60 + om, close = ch * 60 + cm;
    if (close <= open) return null; // overnight hours aren't a thing here
    return { open: open, close: close };
  }

  /* 660 -> "11:00 AM" */
  function label(mins) {
    var h = Math.floor(mins / 60), m = mins % 60;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + ':' + (m < 10 ? '0' + m : m) + ' ' + suffix;
  }

  /* Current wall-clock date/time inside an IANA timezone */
  function nowIn(tz) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());

    var g = {};
    parts.forEach(function (p) { g[p.type] = p.value; });

    var hour = +g.hour === 24 ? 0 : +g.hour; // some engines emit 24 at midnight
    return {
      iso: g.year + '-' + g.month + '-' + g.day,
      y: +g.year, m: +g.month, d: +g.day,
      minutes: hour * 60 + +g.minute
    };
  }

  /* Effective value for a calendar date: dated override, else weekly value */
  function valueFor(data, iso, dowKey) {
    if (data.overrides && Object.prototype.hasOwnProperty.call(data.overrides, iso)) {
      return data.overrides[iso];
    }
    return data.days ? data.days[dowKey] : null;
  }

  /* Walk forward to the next day that actually opens (max 7 days out) */
  function nextOpening(data, now) {
    for (var i = 1; i <= 7; i++) {
      var probe = new Date(Date.UTC(now.y, now.m - 1, now.d + i));
      var iso = probe.toISOString().slice(0, 10);
      var dow = probe.getUTCDay();
      var range = parseRange(valueFor(data, iso, DAY_KEYS[dow]));
      if (range) {
        return { when: i === 1 ? 'tomorrow' : DAY_NAMES[dow], at: label(range.open) };
      }
    }
    return null;
  }

  function computeStatus(data) {
    var now = nowIn(data.tz);
    var todayDow = new Date(Date.UTC(now.y, now.m - 1, now.d)).getUTCDay();
    var raw = valueFor(data, now.iso, DAY_KEYS[todayDow]);
    var range = parseRange(raw);

    if (range) {
      if (now.minutes >= range.open && now.minutes < range.close) {
        return { open: true, text: 'Open now — until ' + label(range.close) };
      }
      if (now.minutes < range.open) {
        return { open: false, text: 'Closed — opens today at ' + label(range.open) };
      }
    } else if (typeof raw === 'string' && raw.trim().toLowerCase() === 'appointment') {
      return { open: false, text: 'Open today by appointment' };
    }

    var next = nextOpening(data, now);
    if (!next) return null; // never opens -> say nothing at all
    var when = next.when === 'tomorrow' ? 'tomorrow' : next.when;
    return { open: false, text: 'Closed — opens ' + when + ' at ' + next.at };
  }

  function init(node) {
    var data;
    try {
      data = JSON.parse(node.getAttribute('data-lh-hours'));
    } catch (e) {
      return; // stays hidden
    }
    if (!data || !data.tz || !data.days) return;

    var status;
    try {
      status = computeStatus(data);
    } catch (e) {
      return; // bad timezone or unexpected shape -> stays hidden
    }
    if (!status) return;

    var dot = node.querySelector('[data-lh-status-dot]');
    var txt = node.querySelector('[data-lh-status-text]');
    if (!dot || !txt) return;

    // Footer-specific classes plus neutral ones, so any section that reuses
    // this script can style the two states without borrowing lh-ft__ names.
    dot.classList.add(status.open ? 'lh-ft__dot--open' : 'lh-ft__dot--closed');
    txt.classList.add(status.open ? 'lh-ft__status-txt--open' : 'lh-ft__status-txt--closed');
    dot.classList.add(status.open ? 'is-open' : 'is-closed');
    txt.classList.add(status.open ? 'is-open' : 'is-closed');
    txt.textContent = status.text;
    node.hidden = false;
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-lh-hours]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-evaluate when the section is re-rendered in the theme editor
  document.addEventListener('shopify:section:load', boot);
})();
